import React from 'react';
import _ from 'lodash';

import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';

import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';
import PageHeader from '../../Common/PageHeader';
import ProcessingIndicator from '../../Common/ProcessingIndicator';

import DataTable from '../../Fields/DataTable';
import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import InputSelectField from '../../Fields/InputSelectField';
import InputDateField from '../../Fields/InputDateField';

import { queryListCredits } from '../../../Queries';

import {
  convertUnixMsToDateInTimezone,
  convertDateToUTC,
} from '../../../Utils/date';
import { getFacility } from '../../../Utils/facility';

import featureFlags from '../../../featureFlags';
import './CreditListPage.scss';

const isEmpty = value => value === null || value === '' || value === undefined;

const creditFilterValues = {
  ALL: 'ALL',
  POSITIVE: 'POSITIVE',
  ZERO: 'ZERO'
};

const creditFilterOptions = [
  { value: creditFilterValues.ALL, label: 'All' },
  { value: creditFilterValues.POSITIVE, label: 'Positive'},
  { value: creditFilterValues.ZERO, label: 'Zero'}
];

const oldCreditOrders = {
  credit_number: { model: 'CREDIT', field: 'readable_id' },
  credit_date: { model: 'CREDIT', field: 'credit_date' },
  payor_id: { model: 'PAYOR', field: 'readable_id' },
  payor_billing_name: { model: 'PAYOR', field: 'billing_lastname' },
  credit_balance: { model: 'CREDIT', field: 'balance' },
  credit_amount: { model: 'CREDIT', field: 'amount' }
}
const creditOrders = featureFlags.SUBSCRIPTION_NAME ? {
  credit_number: { model: 'CREDIT', field: 'readable_id' },
  created_at: { model: 'CREDIT', field: 'created_at' },
  payor_id: { model: 'PAYOR', field: 'readable_id' },
  subscription_name: { model: 'SUBSCRIPTION', field: 'subscription_name' },
  payor_billing_name: { model: 'PAYOR', field: 'billing_lastname' },
  credit_balance: { model: 'CREDIT', field: 'balance' },
  credit_amount: { model: 'CREDIT', field: 'amount' }
} : oldCreditOrders;

if (featureFlags.ACCOUNT_ID) {
  creditOrders.account_id = { model: 'SUBSCRIPTION' , field: 'account_id' };
}

const creditQueryFields = [
  'total',
  'limit',
  'offset',
  { 'credits': [
    'id',
    'readable_id',
    'amount',
    'balance',
    'created_at',
    { 'subscription': [
      featureFlags.ACCOUNT_ID ? 'account_id' : '',
      'subscription_name',
      { 'payor': [
      'readable_id',
      'billing_firstname',
      'billing_lastname'
      ] }
    ] }
  ] }
]


const oldCreditsFilter = {
  creditsFilter: {
    initializationComplete: true,
    inputs: {
      credit_number: null,
      credit_balance: 'ALL',
      credit_date_start: null,
      credit_date_end: null,
      payor_id: null,
      payor_name: null
    }
  }
};

const oldCreditsDataTableColumns = [
  { id: 'credit_number', heading: 'Credit #' },
  { id: 'credit_date', heading: 'Credit Date' },
  { id: 'payor_id', heading: 'Payor ID' },
  { id: 'payor_billing_name', heading: 'Payor Billing Name' },
  { id: 'credit_amount', heading: 'Credit Amount' },
  { id: 'credit_balance', heading: 'Credit Balance' }
];

const creditsDataTableColumns = featureFlags.SUBSCRIPTION_NAME ? [
  { id: 'credit_number', heading: 'Credit #' },
  { id: 'created_at', heading: 'Credit Date' },
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'subscription_name', heading: 'Sub. Name' },
  { id: 'payor_billing_name', heading: 'Billing Name' },
  { id: 'credit_amount', heading: 'Credit Amount' },
  { id: 'credit_balance', heading: 'Credit Balance' }
] : oldCreditsDataTableColumns;

if(featureFlags.ACCOUNT_ID) {
  creditsDataTableColumns.splice(2, 0, { id: 'account_id', heading: 'Acct. ID' });
}

class CreditListPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    credits: null,
    creditsQuery: {
      inProgress: false,
      errorState: false,
      userMessage: null
    },
    creditsFilter: featureFlags.SUBSCRIPTION_NAME ? {
      initializationComplete: true,
      inputs: {
        credit_number: null,
        credit_balance: 'ALL',
        credit_date_start: null,
        credit_date_end: null,
        account_id: null,
        payor_id: null,
        subscription_name: null
      }
    }: oldCreditsFilter
  };

  facilityId;
  facility = null;

  creditsQueryOptions = {
    sortedBy: null,
    limit: null,
    total: null,
    offset: null
  };

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.facilityId = urlSearchParams.get('facility');
  }

  async componentDidMount() {
    const initializationTasks = [];

    initializationTasks.push(this.fetchFacility(this.facilityId));

    try {
      await Promise.all(initializationTasks);

      this.setState({
        pageInitializationComplete: true
      }, () => {
        this.getCredits();
      });
    } catch (errors) {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors.map(({ message }) => message)
      });
    }
  }

  async fetchFacility(facilityId) {
    try {
      this.facility = await getFacility(facilityId);
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [errors];
    }
  }

  getCreditsQueryOptions() {
    const {
      creditsFilter: {
        inputs: {
          credit_number,
          credit_balance,
          credit_date_start,
          credit_date_end,
          subscription_name,
          payor_id,
          payor_name,
          account_id
        }
      }
    } = this.state;

    const queryOptions = {};

    //init expressions
    const filterExpressions = [
      {
        model: 'BILLING_PROFILE',
        op: 'AND',
        filters: [{
          field: 'facility_id',
          op: 'EQ',
          value: `${this.facilityId}`
        }]
      }
    ]
    
    const creditFilterExpression = {
      model: 'CREDIT',
      op: 'AND',
      filters: []
    }
    const payorFilterExpression = {
      model: 'PAYOR',
      op: 'OR',
      filters: []
    }
    const subscriptionFilterExpression = {
      model: 'SUBSCRIPTION',
      op: 'AND',
      filters: []
    }

    //populate credit filter expressions
    if (!isEmpty(credit_number)) {
      creditFilterExpression.filters.push({
        model: 'CREDIT',
        field: 'readable_id',
        op: 'SUBSTRING',
        value: credit_number
      });
    }

    if (!isEmpty(credit_balance) && credit_balance !== creditFilterValues.ALL) {
      creditFilterExpression.filters.push({
        model: 'CREDIT',
        field: 'balance',
        op: credit_balance === creditFilterValues.POSITIVE ? 'GT' : 'EQ',
        value: 0
      });
    }

    if (!isEmpty(credit_date_start)) {
      creditFilterExpression.filters.push({
        model: 'CREDIT',
        field: 'created_at',
        op: 'GTE',
        value: convertDateToUTC(credit_date_start, this.facility.fields.timezone.value)
      });
    }

    if (!isEmpty(credit_date_end)) {
      const endDate = credit_date_end;
      const day = endDate.getDate();
      endDate.setDate(day + 1);

      creditFilterExpression.filters.push({
        model: 'CREDIT',
        field: 'created_at',
        op: 'LT',
        value: convertDateToUTC(endDate, this.facility.fields.timezone.value)
      });
    }

    //populate payor filter expressions
    if (!isEmpty(payor_id)) {
      payorFilterExpression.filters.push({
        model: 'PAYOR',
        field: 'readable_id',
        op: 'SUBSTRING',
        value: payor_id
      });
    }


    if (!featureFlags.SUBSCRIPTION_NAME) {
      if (!isEmpty(payor_name)) {
        const names = payor_name.split(' ');
        for (const name of names) {
          payorFilterExpression.filters.push({
            field: 'billing_firstname',
            op: 'SUBSTRING',
            value: name
          });

          payorFilterExpression.filters.push({
            field: 'billing_lastname',
            op: 'SUBSTRING',
            value: name
          });
        }
      }
    }

    if (featureFlags.SUBSCRIPTION_NAME) {
      //populate subscription expressions
      if (!isEmpty(subscription_name)) {
        subscriptionFilterExpression.filters.push({
          model: 'SUBSCRIPTION',
          field: 'subscription_name',
          op: 'EQ',
          value: subscription_name.trim()
        });
      }
    }

    if (featureFlags.ACCOUNT_ID) {
      if (!isEmpty(account_id)) {
        subscriptionFilterExpression.filters.push({
          model: 'SUBSCRIPTION',
          field: 'account_id',
          op: 'SUBSTRING',
          value:  account_id.trim().replace(/\D+/g, '')
        });
      }
    }

    if (creditFilterExpression.filters.length > 0) filterExpressions.push(creditFilterExpression);
    if (payorFilterExpression.filters.length > 0) filterExpressions.push(payorFilterExpression);
    if (subscriptionFilterExpression.filters.length > 0) filterExpressions.push(subscriptionFilterExpression);

    queryOptions.filter_expressions = filterExpressions;

    if (this.creditsQueryOptions.limit) {
      queryOptions.limit = this.creditsQueryOptions.limit;
    }

    if (this.creditsQueryOptions.offset) {
      queryOptions.offset = this.creditsQueryOptions.offset;
    }

    if (this.creditsQueryOptions.sortedBy) {
      queryOptions.order = creditOrders[this.creditsQueryOptions.sortedBy.columnId];
      queryOptions.order.direction = this.creditsQueryOptions.sortedBy.direction;
    }

    return queryOptions;
  }

  getCreditsDataTableRows(credits) {
    return credits.map(credit => {
      const oldCreditsDataTableRows = [
        `<a href="?page=parkchirp_credit&credit=${credit.id}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}">${credit.readable_id}</a>`,
        convertUnixMsToDateInTimezone(credit.created_at, this.facility.fields.timezone.value),
        credit.subscription.payor.readable_id,
        `${credit.subscription.payor.billing_firstname} ${credit.subscription.payor.billing_lastname}`,
        (credit.amount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
        (credit.balance / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      ];

      const newCreditsDataTableRows = featureFlags.SUBSCRIPTION_NAME ?
      [
        `<a href="?page=parkchirp_credit&credit=${credit.id}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}">${credit.readable_id}</a>`,
        convertUnixMsToDateInTimezone(credit.created_at, this.facility.fields.timezone.value),
        credit.subscription.payor.readable_id,
        credit.subscription.subscription_name,
        `${credit.subscription.payor.billing_firstname} ${credit.subscription.payor.billing_lastname}`,
        (credit.amount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
        (credit.balance / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      ] : oldCreditsDataTableRows;

      if (featureFlags.ACCOUNT_ID) {
        newCreditsDataTableRows.splice(2, 0, 'AC-'+credit.subscription.account_id);
      }

      return newCreditsDataTableRows;
    });
  }

  handleCreditsDataTableSortRequest = data => {
    this.creditsQueryOptions.sortedBy = data;
    this.getCredits();
  }

  handleCreditsDataTableLimitChange = data => {
    this.creditsQueryOptions.limit = data;
    this.creditsQueryOptions.offset = null;
    this.getCredits();
  }

  handleCreditsDataTablePageChange = data => {
    let offset = ((data - 1) * this.creditsQueryOptions.limit);
    this.creditsQueryOptions.offset = offset;
    this.getCredits();
  }

  getCredits() {
    const creditsQuery = {
      inProgress: true,
      errorState: false,
      userMessage: null
    };

    this.setState({ creditsQuery }, async () => {
      const options = this.getCreditsQueryOptions();
      const response = await queryListCredits(options, creditQueryFields);
      if (response.errors) {
        const creditsQuery = {
          inProgress: false,
          errorState: true,
          userMessage: 'An error occurred while retrieving the list of credits.'
        }
        this.setState({ creditsQuery });
        return;
      }

      this.creditsQueryOptions.total = response.data.listCredits.total;
      const credits = this.getCreditsDataTableRows(response.data.listCredits.credits);
      const creditsQuery = {
        inProgress: false,
        errorState: false,
        userMessage: null
      }

      this.setState({ credits, creditsQuery });
    });
  }

  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getCreditListPageBreadcrumb());

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }

  renderPageHeader() {
    const facilityParam = (this.facilityId === null) ? '' :
      `&facility=${this.facilityId}`;

    const heading = 'Credits';
    const subheading = (this.facility === null) ? null :
      this.facility.fields.facility_name.value;

    const actions = [
      <a href={`?page=parkchirp_credit${facilityParam}`}
        className="button button-primary">
          Create New Credit
      </a>
    ];
    return (
      <PageHeader
        heading={heading}
        subheading={subheading}
        actions={actions}
      />
    );
  }

  renderPageInitializationBlock() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <>
        {(!pageInitializationComplete) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while initializing the page:</p>
              <ul>
                {pageInitializationErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
      </>
    );
  }

  renderPageMainContent() {
    return (
      <>
        {this.renderFilterForm()}
        {this.renderCreditsTable()}
      </>
    )
  }

  renderCreditsTable() {
    const { credits, creditsQuery } = this.state;

    if (creditsQuery.inProgress) {
      return (
        <div className="parkchirp--page--user-message-container">
          <ProcessingIndicator userMessage="Retrieving Credits..." />
        </div>
      );
    }

    if (creditsQuery.errorState) {
      return (
        <div className="parkchirp--page--user-message-container">
          <p>{creditsQuery.userMessage}</p>
        </div>
      );
    }

    if (credits !== null) {
      return (
        <div className="parkchirp--credit-list-page--credit-table">
          <DataTable id="credit-list-page"
            columns={creditsDataTableColumns}
            rows={credits}
            overallRowCount={this.creditsQueryOptions.total}
            currentOffset={this.creditsQueryOptions.offset}
            currentLimit={this.creditsQueryOptions.limit}
            sortedBy={this.creditsQueryOptions.sortedBy}
            onSortRequest={this.handleCreditsDataTableSortRequest}
            onLimitChange={this.handleCreditsDataTableLimitChange}
            onPageChange={this.handleCreditsDataTablePageChange}
          />
        </div>
      );
    }

    return null;
  }

  resetCreditsFilterForm() {
    const inputs = _.cloneDeep(this.state.creditsFilter.inputs);
    for (let input in inputs) {
      inputs[input] = null;
    }

    const creditsFilter = _.cloneDeep(this.state.creditsFilter);
    creditsFilter.inputs = inputs;

    this.setState({ creditsFilter }, this.getCredits);
  }

  handleCreditsFilterFormSubmitClick = () => {
    this.getCredits();
  }

  handleCreditsFilterFormResetClick = () => {
    this.resetCreditsFilterForm();
  }

  _getCreditsFilterInputOnChange(inputId) {
    return value => {
      const inputs = _.cloneDeep(this.state.creditsFilter.inputs);
      inputs[inputId] = value;

      const creditsFilter = _.cloneDeep(this.state.creditsFilter);
      creditsFilter.inputs = inputs;

      this.setState({ creditsFilter });
    }
  }

  renderFilterForm() {
    const { creditsFilter } = this.state;

    return (
      <div className="parkchirp--credit-list-page--filter-form">
        <h2>Filter</h2>
        <div className="parkchirp--credit-list-page--filter-inputs">
          <div className="parkchirp--credit-list-page--filter-input-block">
            <Field label="Credit #"
              id="parkchirp--credit-list-page-filter--credit-num"
              layout="columns">
              <InputTextField inputId="parkchirp--credit-list-page-filter--credit-num"
                value={creditsFilter.inputs.credit_number}
                onChange={this._getCreditsFilterInputOnChange('credit_number')}
                editMode={creditsFilter.initializationComplete}
              />
            </Field>
            <Field label="Balance"
              id="parkchirp--credit-list-page-filter--credit-balance"
              layout="columns">
              <InputSelectField
                options={creditFilterOptions}
                value={creditsFilter.inputs.credit_balance}
                onChange={this._getCreditsFilterInputOnChange('credit_balance')}
                editMode={creditsFilter.initializationComplete}
              />
            </Field>
          </div>
          <div className="parkchirp--credit-list-page--filter-input-block">
            <Field label="Start Date"
              id="parkchirp--credit-list-page-filter--credit-date-start"
              layout="columns">
              <InputDateField inputId="parkchirp--credit-list-page-filter--credit-date-start"
                value={creditsFilter.inputs.credit_date_start}
                onChange={this._getCreditsFilterInputOnChange('credit_date_start')}
                editMode={creditsFilter.initializationComplete}
              />
            </Field>
            <Field label="End Date"
              id="parkchirp--credit-list-page-filter--credit-date-end"
              layout="columns">
              <InputDateField inputId="parkchirp--credit-list-page-filter--credit-date-end"
                value={creditsFilter.inputs.credit_date_end}
                onChange={this._getCreditsFilterInputOnChange('credit_date_end')}
                editMode={creditsFilter.initializationComplete}
              />
            </Field>
          </div>
          <div className="parkchirp--credit-list-page--filter-input-block">
          {featureFlags.ACCOUNT_ID && 
                <Field label="Account ID"
                  id="parkchirp--credit-list-page-filter--account-id"
                  layout="columns">
                  <InputTextField inputId="parkchirp--subscription-list-filter--account-id"
                    value={creditsFilter.inputs.account_id}
                    onChange={this._getCreditsFilterInputOnChange('account_id')}
                    editMode={creditsFilter.initializationComplete}
                  />
                </Field>
              }
            <Field label="Sub. ID"
              id="parkchirp--credit-list-page-filter--payor-id"
              layout="columns">
              <InputTextField inputId="parkchirp--credit-list-page-filter--payor-id"
                value={creditsFilter.inputs.payor_id}
                onChange={this._getCreditsFilterInputOnChange('payor_id')}
                editMode={creditsFilter.initializationComplete}
              />
            </Field>

            {!featureFlags.SUBSCRIPTION_NAME &&
              <Field label="Payor Billing Name"
                id="parkchirp--credit-list-page-filter--payor-name"
                layout="columns">
                <InputTextField inputId="parkchirp--credit-list-page-filter--payor-name"
                  value={creditsFilter.inputs.payor_name}
                  onChange={this._getCreditsFilterInputOnChange('payor_name')}
                  editMode={creditsFilter.initializationComplete}
                />
              </Field>
            }

            {featureFlags.SUBSCRIPTION_NAME &&
              <Field label="Sub. Name"
                id="parkchirp--credit-list-page-filter--sub-name"
                layout="columns">
                <InputTextField inputId="parkchirp--credit-list-page-filter--sub-name"
                  value={creditsFilter.inputs.subscription_name}
                  onChange={this._getCreditsFilterInputOnChange('subscription_name')}
                  editMode={creditsFilter.initializationComplete}
                />
              </Field>
            }

          </div>
        </div>
        <div className="parkchirp--credit-list-page--filter-actions">
          <button type="button"
            className="button button-primary parkchirp--credit-list-page--filter-action"
            onClick={this.handleCreditsFilterFormSubmitClick}>
            Filter
          </button>
          <button type="button"
            className="button parkchirp--credit-list-page--filter-action"
            onClick={this.handleCreditsFilterFormResetClick}>
            Reset
          </button>
        </div>
      </div>
    );
  }

  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page parkchirp--page--credit-list">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        <div className="parkchirp--page-content">
          {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
            this.renderPageInitializationBlock()
          }
          {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
            this.renderPageMainContent()
          }
        </div>
      </div>
    );
  }
}

export function getCreditListPageBreadcrumb(currentPage = true, facilityId = null) {
  const title = 'Credits';
  const href = (currentPage) ? null :
    `?page=parkchirp_credits${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

    return { title, href };
}

export default CreditListPage;